import { FolderResponse } from '../schemas'
import { FolderOverflowMenu } from './FolderOverflowMenu'
import { DriveListItem } from '@/user_files/drive/components/drive-list-item/DriveListItem'
import { formatDriveListFileCount, formatDriveListUpdatedAtParts } from '@/user_files/utils/drive-list-format'
import { matterPaths } from '@/routing/paths'
import { useNavigate, useParams } from 'react-router-dom'
import { isLocalStorageItem, isMatterFilesDeleteEligible } from '@/user_files/external-storage/utils/external-storage-items'
import { DisconnectedFolderIcon, DisconnectedFromProviderTag } from '@/user_files/external-storage/components/DisconnectedStorageItem'

interface FolderListItemProps {
  folder: FolderResponse
  /** Containing folder name, shown only in the search results table. */
  locationLabel?: string
  /** Whole trail from the drive root, shown on hover over the Location cell. */
  locationPath?: string
  disabled?: boolean
  selected?: boolean
  onToggleSelect?: () => void
  onRename?: () => void
  onMove?: () => void
  onDelete?: () => void
  /** Overrides the default navigate-into-folder, for rows whose contents live in another view. */
  onOpen?: () => void
  /** Set for a synced row: no selection, no actions, and the note explaining why. */
  readOnlyNote?: string
  /** Whether this folder is a direct child of the matter-owned root folder. */
  isMatterRootChild?: boolean
}

/**
 * FolderListItem
 *
 * Single folder row in the folder list. Clicking the row navigates into that folder.
 * Checkbox click toggles selection without navigating.
 *
 * Navigation is user-action-driven (not effect-driven) to ensure clean browser history.
 * The URL change triggers the URL → Redux sync effect in UserFilesFolderRouteHandler.
 */
export function FolderListItem({
  folder,
  locationLabel,
  locationPath,
  disabled,
  selected,
  onToggleSelect,
  onRename,
  onMove,
  onDelete,
  onOpen,
  readOnlyNote,
  isMatterRootChild = false,
}: FolderListItemProps) {
  const navigate = useNavigate()
  const { matterId } = useParams<{ matterId: string }>()

  const handleClick = () => {
    if (onOpen) {
      onOpen()
      return
    }

    if (!matterId) return

    // Navigate directly - URL change will trigger Redux sync
    navigate(matterPaths.userFiles.folder(matterId, folder.id))
  }

  const { date, time } = formatDriveListUpdatedAtParts(folder.updated_at)
  const isExternal = !isLocalStorageItem(folder)
  const canDelete = isMatterFilesDeleteEligible(folder)
  const disconnectedRootMetadata = isMatterRootChild && folder.external_storage?.tombstoned ? folder.external_storage : undefined

  return (
    <DriveListItem
      variant="folder"
      name={folder.name}
      formattedDate={date}
      formattedTime={time}
      // Folders have no byte size, so Size shows the recursive file count the API returns — which is
      // also what the Size column sorts folders by.
      detailsLabel={formatDriveListFileCount(folder.file_count)}
      locationLabel={locationLabel}
      locationPath={locationPath}
      folderIcon={disconnectedRootMetadata ? <DisconnectedFolderIcon provider={disconnectedRootMetadata.provider} /> : undefined}
      nameMetadata={
        disconnectedRootMetadata ? (
          <DisconnectedFromProviderTag provider={disconnectedRootMetadata.provider} disconnectedAt={disconnectedRootMetadata.updated_at} />
        ) : undefined
      }
      selectionDisabledReason={readOnlyNote}
      // Keep the checkbox cell: this table's header always has a select column, so omitting the
      // cell would shift every following cell one column left.
      disabled={disabled || !canDelete}
      selected={selected}
      onToggleSelect={onToggleSelect}
      onClick={handleClick}
      /*
        External folders never gain Rename or Move. Detached folders expose Delete only; managed
        folders have no actions and keep their explanation on the disabled selection control.
      */
      overflowMenu={
        isExternal ? (
          <FolderOverflowMenu onDelete={canDelete ? onDelete : undefined} />
        ) : (
          <FolderOverflowMenu onRename={onRename} onMove={onMove} onDelete={onDelete} />
        )
      }
    />
  )
}
